/**
 * Alta y administración de usuarios (`/api/users`).
 *
 * El alta es PREVIA al login: el acceso con Google solo se acepta si el email ya
 * existe en `users`. No hay auto-registro, y una identidad de Firebase válida sin
 * alta previa recibe 404.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http } from '@/services/http'
import type { HttpResult } from '@/services/http'
import type { Usuario } from '@/interfaces/Usuario'

/** Forma con la que el API de registro usuarios devuelve este dato. */
export interface RolApi {
  id: number
  name: string
  label: string
  descripcion?: string | null
  es_sistema: boolean
  nivel_aplicacion: string
  permissions_count?: number
  permisos?: string[]
}

/** Forma con la que el API de registro usuarios devuelve este dato. */
export interface PermisoApi {
  id: number
  name: string
  label: string
  descripcion?: string | null
}

/** Forma con la que el API de registro usuarios devuelve este dato. */
export interface ModuloApi {
  id: number
  nombre: string
  permisos: PermisoApi[]
}

/** Forma con la que el API de registro usuarios devuelve este dato. */
export interface MatrizPermisos {
  modulos: ModuloApi[]
  roles: (RolApi & { permisos: string[] })[]
}

/** Forma con la que el API de registro usuarios devuelve este dato. */
export interface AsignacionRolEntry {
  id: number
  user_id: number
  role_id: number
  asignado_por: number
  observaciones?: string | null
  created_at: string
  role: { id: number; name: string; label: string }
  autor: { id: number; name: string; email: string }
}

// ─── Usuarios ───

export async function listarUsuarios(): Promise<Usuario[]> {
  const result = await http.get<Usuario[]>('/api/users')
  return result.ok ? (result.data ?? []) : []
}

/**
 * Crea registro usuarios · `POST /api/users`.
 *
 * @returns HttpResult<Usuario>
 */
export async function registrarUsuario(payload: {
  name: string
  email: string
  telephone?: string
}): Promise<HttpResult<Usuario>> {
  return http.post<Usuario>('/api/users', payload)
}

/**
 * Elimina registro usuarios · `DELETE /api/users`.
 *
 * @returns HttpResult<unknown>
 */
export async function eliminarUsuario(id: number): Promise<HttpResult<unknown>> {
  return http.delete(`/api/users/${id}`)
}

/**
 * Actualiza registro usuarios · `PATCH /api/users`.
 *
 * @returns HttpResult<{ message: string }>
 */
export async function actualizarEstadoUsuario(
  id: number,
  estado: 'activo' | 'inactivo'
): Promise<HttpResult<{ message: string }>> {
  return http.patch<{ message: string }>(`/api/users/${id}/estado`, { estado })
}

/**
 * Asigna registro usuarios · `POST /api/users`.
 *
 * @returns HttpResult<{ message: string; roles: string[] }>
 */
export async function asignarRolesUsuario(
  id: number,
  roles: string[],
  observaciones?: string
): Promise<HttpResult<{ message: string; roles: string[] }>> {
  const body: Record<string, unknown> = { roles }
  if (observaciones) body.observaciones = observaciones
  return http.post(`/api/users/${id}/roles`, body)
}

/**
 * Operación sobre registro usuarios · `GET /api/users`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns AsignacionRolEntry[]
 */
export async function historialRolesUsuario(id: number): Promise<AsignacionRolEntry[]> {
  const result = await http.get<AsignacionRolEntry[]>(`/api/users/${id}/historial-roles`)
  return result.ok ? (result.data ?? []) : []
}

// ─── Roles ───

export async function listarRoles(): Promise<RolApi[]> {
  const result = await http.get<RolApi[]>('/api/roles')
  return result.ok ? (result.data ?? []) : []
}

/**
 * Crea registro usuarios · `POST /api/roles`.
 *
 * @returns HttpResult<RolApi>
 */
export async function crearRol(payload: {
  name: string
  label: string
  descripcion?: string
  nivel_aplicacion: string
  permisos?: string[]
}): Promise<HttpResult<RolApi>> {
  return http.post<RolApi>('/api/roles', payload)
}

/**
 * Actualiza registro usuarios.
 */
export async function actualizarRol(
  id: number,
  payload: {
    name?: string
    label?: string
    descripcion?: string
    nivel_aplicacion?: string
    permisos?: string[]
  }
): Promise<HttpResult<RolApi>> {
  return http.put<RolApi>(`/api/roles/${id}`, payload)
}

/**
 * Elimina registro usuarios · `DELETE /api/roles`.
 *
 * @returns HttpResult<unknown>
 */
export async function eliminarRol(id: number): Promise<HttpResult<unknown>> {
  return http.delete(`/api/roles/${id}`)
}

// ─── Permisos / Matriz ───

export async function obtenerMatrizPermisos(): Promise<MatrizPermisos | null> {
  const result = await http.get<MatrizPermisos>('/api/modulos/matriz-permisos')
  return result.ok ? (result.data ?? null) : null
}

// ─── Permisos CRUD ───

export interface PermisoApiDetalle {
  id: number
  name: string
  label: string
  descripcion?: string | null
  modulo_id: number
  es_sistema: boolean
  modulo?: { id: number; nombre: string }
}

/**
 * Lista registro usuarios · `GET /api/permisos`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns PermisoApiDetalle[]
 */
export async function listarPermisos(): Promise<PermisoApiDetalle[]> {
  const result = await http.get<PermisoApiDetalle[]>('/api/permisos')
  return result.ok ? (result.data ?? []) : []
}

/**
 * Crea registro usuarios · `POST /api/permisos`.
 *
 * @returns HttpResult<PermisoApiDetalle>
 */
export async function crearPermiso(payload: {
  name: string
  label: string
  descripcion?: string
  modulo_id: number
}): Promise<HttpResult<PermisoApiDetalle>> {
  return http.post<PermisoApiDetalle>('/api/permisos', payload)
}

/**
 * Actualiza registro usuarios.
 */
export async function actualizarPermiso(
  id: number,
  payload: {
    name?: string
    label?: string
    descripcion?: string
    modulo_id?: number
  }
): Promise<HttpResult<PermisoApiDetalle>> {
  return http.put<PermisoApiDetalle>(`/api/permisos/${id}`, payload)
}

/**
 * Elimina registro usuarios · `DELETE /api/permisos`.
 *
 * @returns HttpResult<unknown>
 */
export async function eliminarPermiso(id: number): Promise<HttpResult<unknown>> {
  return http.delete(`/api/permisos/${id}`)
}

/**
 * Lista registro usuarios · `GET /api/modulos/matriz-permisos`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns { id: number; nombre: string }[]
 */
export async function listarModulos(): Promise<{ id: number; nombre: string }[]> {
  const result = await http.get<MatrizPermisos>('/api/modulos/matriz-permisos')
  return result.ok ? (result.data?.modulos.map(m => ({ id: m.id, nombre: m.nombre })) ?? []) : []
}
